import { useLocation, useNavigate } from "react-router-dom";
import { User2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import PillNav from "@/components/reactbits/PillNav";
import ThemeToggle from "@/components/ThemeToggle";
import { NAV_LINKS as links, activeHref as resolveActiveHref } from "@/lib/nav";

export default function Navbar() {
  const { pathname } = useLocation();
  const { user, isPro } = useAuth();
  const navigate = useNavigate();

  const activeHref = resolveActiveHref(pathname, links);

  // Not a nav item: it changes with auth state and it is an action, not a
  // destination in the same sense. PillNav renders it in its own slot.
  // Matched to the pills next to them, measured rather than guessed: 36px tall,
  // 16px semibold, 18px of horizontal padding, fully rounded. Account keeps an
  // outline instead of a fill — same shape, but it opens a page about you
  // rather than being another place to go.
  // Matched to the pills beside them, measured off a rendered pill rather than
  // guessed. Outlined like the rest, and deliberately NOT filled: filled is how
  // this nav says "you are here", and neither of these is a page you're on.
  // currentColor rather than a literal: the outline is the same ink as the
  // label, so it follows the theme without naming a colour twice.
  const pillMetrics =
    "inline-flex h-9 items-center rounded-full px-[18px] text-base font-semibold " +
    "text-ink shadow-[inset_0_0_0_1.5px_currentColor] transition-[transform,background-color,color] " +
    "duration-150 ease-out active:scale-[0.97] hover:bg-ink hover:text-paper";

  // Built per slot rather than once and rendered twice. The desktop bar and the
  // mobile menu both carry this control, and both are always in the DOM with one
  // hidden per breakpoint — sharing a test id made every selector for it match
  // two elements.
  const authControl = (suffix) => (user ? (
    <button onClick={() => navigate("/account")} data-testid={`nav-account${suffix}`}
      className={`${pillMetrics} gap-1.5`}>
      <User2 size={16} />
      {isPro && <span className="rounded bg-ink px-1.5 py-0.5 text-[10px] font-bold text-paper">PRO</span>}
      <span>Account</span>
    </button>
  ) : user === false ? (
    <Button data-testid={`nav-signin${suffix}`} onClick={() => navigate("/signin")}
      className={`${pillMetrics} bg-transparent`}>
      Sign in
    </Button>
  ) : null);

  // The theme control sits after the auth control and stays put while auth
  // resolves — it works logged in or out, so it shouldn't pop in late.
  const trailing = (
    <div className="flex items-center gap-2">
      {authControl("")}
      <ThemeToggle />
    </div>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-line/10 bg-surface/90 backdrop-blur" data-testid="navbar">
      <div className="mx-auto max-w-6xl px-5 py-3">
        <PillNav
          items={links}
          activeHref={activeHref}
          trailing={trailing}
          // Same two controls on a phone, split across the two places a phone
          // nav has. The theme toggle stays in the bar because it is a switch
          // rather than a destination — burying a one-tap control behind a menu
          // is the version of this that costs two. Sign in / Account is a
          // destination and goes with the other destinations, where it also has
          // room for the PRO badge that the bar does not.
          mobileBar={<ThemeToggle testId="theme-toggle-mobile" />}
          // Widened and given a thumb's worth of height for the menu. The same
          // node is a 36px pill in the desktop bar, where it sits in a row of
          // pills; on its own at the foot of a menu it should match the rows
          // above it rather than float in the corner at two thirds their size.
          mobileMenuFooter={user !== null && (
            <div className="w-full [&>button]:h-11 [&>button]:w-full [&>button]:justify-center">
              {authControl("-mobile")}
            </div>
          )}
          pillGap="10px"
          // Outlined pills on paper; the current page is the filled one, and
          // hovering any other previews that fill. The component's own default
          // is a dark slab of pills, which on this site reads as a different
          // product's chrome.
          //
          // Tokens rather than literals, so the whole arrangement inverts with
          // the theme: in dark mode the resting pills are the dark ones and the
          // page you're on is the light one, which is the same statement.
          baseColor="transparent"
          pillColor="rgb(var(--c-surface))"
          pillTextColor="rgb(var(--c-ink))"
          pillOutlineColor="rgb(var(--c-ink))"
          activePillColor="rgb(var(--c-ink))"
          activePillTextColor="rgb(var(--c-paper))"
          hoverPillColor="rgb(var(--c-ink))"
          hoveredPillTextColor="rgb(var(--c-paper))"
          className="[&_a]:!normal-case [&_span]:!normal-case"
          logoNode={
            <span className="flex items-center gap-2" data-testid="nav-logo">
              {/* The brand mark itself rather than a stand-in glyph — same
                  artwork as favicon.svg and the icon set, inlined so the tile
                  can follow the theme.

                  The tile is `fill-ink`, not the file's literal #0F172A: in
                  light mode --c-ink resolves to exactly that colour, and in dark
                  it inverts to a light tile, which is what the telescope square
                  here used to do. Left as the hex it would sit at #0F172A on a
                  #1E293B bar and read as a smudge. A Tailwind utility rather
                  than fill="rgb(var(--c-ink))" because that lands on an SVG
                  presentation attribute, where a CSS variable is not reliably
                  resolved.

                  The slash keeps its literal red and blue. Those are the lean
                  axis the gauge uses, not decoration, so they hold in both
                  themes for the same reason the gauge's gradient does. */}
              <svg viewBox="0 0 100 100" className="h-8 w-8 shrink-0" aria-hidden="true" focusable="false">
                <rect width="100" height="100" rx="16" className="fill-ink" />
                <path d="M60.5 13 H81 L62.8 50 H42.3 Z" fill="#DC2626" />
                <path d="M42.3 50 H62.8 L44.6 87 H24.1 Z" fill="#2563EB" />
              </svg>
              <span className="font-serif text-xl tracking-tight text-ink" style={{ fontWeight: 800 }}>
                SlantScan
              </span>
            </span>
          }
        />
      </div>
    </header>
  );
}
